import { BROKER_SYNC_RUN_STATUS_OBJ } from "@signals/shared";
import { BROKER_SYNC_PROVIDER_LABELS, BROKER_SYNC_RUN_STATUS_LABELS } from "@signals/shared";
import type { BrokerSyncAccountStatus } from "./broker-sync-panel.types";
import { localeKey, t, formatDateTime } from "./broker-sync-panel-format.util";

export function AccountSyncStatus({ account }: { account: BrokerSyncAccountStatus }) {
  const status = account.latestRun?.status;
  const localizedStatus = status
    ? BROKER_SYNC_RUN_STATUS_LABELS[status]?.[localeKey()] ?? status
    : t("portfolio.brokerSync.neverRun");
  const counterLabels: Partial<Record<string, Parameters<typeof t>[0]>> = {
    imported: "portfolio.brokerSync.counterImported",
    created: "portfolio.brokerSync.counterCreated",
    updated: "portfolio.brokerSync.counterUpdated",
    appliedSplits: "portfolio.brokerSync.counterAppliedSplits",
    disappeared: "portfolio.brokerSync.counterDisappeared",
    pairedTransfer: "portfolio.brokerSync.counterPairedTransfer",
    deferredTransfer: "portfolio.brokerSync.counterDeferredTransfer",
    standaloneTransfer: "portfolio.brokerSync.counterStandaloneTransfer",
    outOfScope: "portfolio.brokerSync.counterOutOfScope",
    unsupported: "portfolio.brokerSync.counterUnsupported",
    invalidPayload: "portfolio.brokerSync.counterInvalidPayload",
    unmatchedInstrument: "portfolio.brokerSync.counterUnmatchedInstrument",
    instrumentCreateCandidate: "portfolio.brokerSync.counterInstrumentCandidate",
    currencyUnresolved: "portfolio.brokerSync.counterCurrencyUnresolved",
    corporateAction: "portfolio.brokerSync.counterCorporateAction",
    suppressedCommission: "portfolio.brokerSync.counterSuppressedCommission",
    discardedBeforeCutoff: "portfolio.brokerSync.counterBeforeCutoff",
    dividendsWithUnconfirmedBasis: "portfolio.brokerSync.counterDividendsUnconfirmedBasis",
    symbolsCreated: "portfolio.brokerSync.counterSymbolsCreated",
    providerIssues: "portfolio.brokerSync.counterProviderIssues",
    retryOperations: "portfolio.brokerSync.counterRetryOperations",
    "finam.transactionsWithTradeBlocksSkipped": "portfolio.brokerSync.counterFinamTradeBlocks",
    "ibkr.tradesAggregateRowsSkipped": "portfolio.brokerSync.counterIbkrAggregateSkipped",
    "ibkr.cashTransactionsAggregateRowsSkipped": "portfolio.brokerSync.counterIbkrAggregateSkipped",
    "ibkr.corporateActionsAggregateRowsSkipped": "portfolio.brokerSync.counterIbkrAggregateSkipped",
    "ibkr.tradesAggregateLevelFallback": "portfolio.brokerSync.counterIbkrAggregateFallback",
    "ibkr.cashTransactionsAggregateLevelFallback": "portfolio.brokerSync.counterIbkrAggregateFallback",
    "ibkr.corporateActionsAggregateLevelFallback": "portfolio.brokerSync.counterIbkrAggregateFallback",
  };
  const visibleCounters = Object.entries(account.latestRun?.counters ?? {})
    .filter(([key, value]) => value !== 0 && counterLabels[key])
    .map(([key, value]) => ({ label: t(counterLabels[key]!), value }));
  return (
    <article className="space-y-2 rounded-md border p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h4 className="font-semibold">{account.accountName}</h4>
        <span className="text-sm text-muted-foreground">
          {BROKER_SYNC_PROVIDER_LABELS[account.provider][localeKey()]}
        </span>
      </div>
      <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
        <div><dt className="text-muted-foreground">{t("portfolio.brokerSync.brokerAccountId")}</dt><dd className="font-mono">{account.brokerAccountId}</dd></div>
        <div><dt className="text-muted-foreground">{t("portfolio.brokerSync.fromDate")}</dt><dd>{account.brokerSyncFrom}</dd></div>
        <div><dt className="text-muted-foreground">{t("portfolio.brokerSync.lastRun")}</dt><dd>{account.latestRun ? `${localizedStatus} · ${formatDateTime(account.latestRun.finishedAt ?? account.latestRun.startedAt)}` : localizedStatus}</dd></div>
        <div><dt className="text-muted-foreground">{t("portfolio.brokerSync.openIssues")}</dt><dd>{account.openIssueCount}</dd></div>
        <div><dt className="text-muted-foreground">{t("portfolio.brokerSync.intentionallySkipped")}</dt><dd>{account.intentionallySkippedCount}</dd></div>
        <div><dt className="text-muted-foreground">{t("portfolio.brokerSync.pendingTransfers")}</dt><dd>{account.pendingTransferPairCount}</dd></div>
      </dl>
      {account.latestRun && visibleCounters.length > 0 ? (
        <div className="space-y-1 text-sm text-muted-foreground">
          <p>
            <span className="font-medium">{t("portfolio.brokerSync.runResults")}: </span>
            {visibleCounters.map((item, index) => <span key={index}>{index > 0 ? " · " : ""}{item.label}: {item.value}</span>)}
          </p>
          <details>
            <summary className="cursor-pointer">{t("portfolio.brokerSync.counterHelpTitle")}</summary>
            <p className="mt-1">{t("portfolio.brokerSync.counterHelp")}</p>
          </details>
        </div>
      ) : account.latestRun ? (
        <p className="text-sm text-muted-foreground">{t("portfolio.brokerSync.noRunCounters")}</p>
      ) : null}
      {account.latestRun?.status === BROKER_SYNC_RUN_STATUS_OBJ.FAILED && account.latestRun.error ? (
        <p className="text-sm text-destructive">
          <span className="font-medium">{t("portfolio.brokerSync.runError")}: </span>
          {account.latestRun.error}
        </p>
      ) : null}
    </article>
  );
}
